import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { apiRequest } from '../../fetchSetup';

import {
  Box,
  Button,
  Snackbar,
  Paper,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
// import { MedicineInsertionForm } from "./MedicinesInsertionForm";
import MedicineInsertionForm from "./MedicinesInsertionForm"
const addingMedicineAPI = process.env.REACT_APP_ADD_MEDICINES_ADMIN;

function CustomToolbar({ onAdd }) {
  return (
    <GridToolbarContainer>
      <Button
        variant="contained"
        startIcon={<AddCircleIcon />}
        onClick={onAdd}
        sx={{
          backgroundColor: "#087c7c",
          textTransform: "none",
          borderRadius: "10px",
          px: 2.5,
          py: 1,
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "#066868",
          },
        }}
      >
        Add Medicine
      </Button>
    </GridToolbarContainer>
  );
}

export default function AddMedicinesAdmin() {
  const [medicines, setMedicines] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
  const [loading, setLoading] = useState(true);

  async function fetchMedicines() {
    try {
      setLoading(true);
      const res = await apiRequest(addingMedicineAPI);
      const data = await res.json();
      setMedicines(data.medicines || []);
    } catch (error) {
      setSnack({ open: true, message: "Failed to fetch medicines", severity: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchMedicines();
  }, []);

  const handleFormSubmit = async (medicine) => {
    try {
      const method = selectedMedicine ? "PUT" : "POST";
      const url = selectedMedicine
        ? `${addingMedicineAPI}/${selectedMedicine.medicine_id}`
        : addingMedicineAPI;

      const res = await apiRequest(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(medicine),
      });

      if (res.ok) {
        setSnack({
          open: true,
          message: selectedMedicine ? "Medicine updated!" : "Medicine added!",
          severity: "success",
        });
        fetchMedicines();
        setFormOpen(false);
        setSelectedMedicine(null);
      } else {
        setSnack({
          open: true,
          message: "Error adding/updating medicine.",
          severity: "error",
        });
      }
    } catch {
      setSnack({ open: true, message: "Request failed.", severity: "error" });
    }
  };

  const handleDelete = async (med) => {
    if (!window.confirm("Delete this medicine?")) return;
    const res = await apiRequest(`${addingMedicineAPI}/${med.medicine_id}`, { method: "DELETE" });
    if (res.ok) {
      setSnack({ open: true, message: "Medicine deleted!", severity: "success" });
      fetchMedicines();
    } else {
      setSnack({ open: true, message: "Delete failed.", severity: "error" });
    }
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1.3, minWidth: 160 },
    { field: "manufacturer", headerName: "Manufacturer", flex: 1.2, minWidth: 140 },
    { field: "category", headerName: "Category", flex: 1, minWidth: 130 },
    { field: "price", headerName: "Price (₹)", flex: 0.7, minWidth: 90 },
    { field: "discounted_price", headerName: "Discounted (₹)", flex: 0.9, minWidth: 110 },
    { field: "stock", headerName: "Stock", flex: 0.6, minWidth: 80 },
    {
      field: "is_active",
      headerName: "Active",
      flex: 0.7,
      minWidth: 90,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: params.value ? "green" : "red",
            fontWeight: 600,
          }}
        >
          {params.value ? "Yes" : "No"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      flex: 0.7,
      minWidth: 110,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon color="primary" />}
          label="Edit"
          onClick={() => {
            setSelectedMedicine(params.row);
            setFormOpen(true);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon color="error" />}
          label="Delete"
          onClick={() => handleDelete(params.row)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            fontWeight: 700,
            color: "#087c7c",
            textAlign: "center",
          }}
        >
          Medicine Management
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={medicines}
            columns={columns}
            pageSize={10}
            getRowId={(row) => row.medicine_id || row.id}
            disableSelectionOnClick
            autoHeight
            components={{
              Toolbar: () => <CustomToolbar onAdd={() => { setFormOpen(true); setSelectedMedicine(null); }} />,
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#e6f4f4",
                color: "#003f3f",
                fontWeight: "bold",
                fontSize: "0.95rem",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f2fdfd",
              },
            }}
          />
        )}
      </Paper>

      <MedicineInsertionForm
        open={formOpen}
        handleClose={() => setFormOpen(false)}
        initialData={selectedMedicine}
        handleSubmit={handleFormSubmit}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
